import { memo, useMemo } from 'react';

import { Status } from '../../../../../fsm/jobs';
import { statusPalette } from '../../../utils/statusPalette';

import styles from './index.module.css';

/**
 * The pipeline a candidate actually moves through, in order.
 *
 * NOT the reference mockup's stages. That mockup shows "R1 Screen / R2
 * Interview / In-Person / HM Review / Decision", which is a generic recruiting
 * funnel and not this product's. Avrio's is Rio-specific: the candidate is
 * invited, Rio screens them, a human may run an alternate interview, then the
 * hiring manager reviews. Ledger C18 is explicit that the machine advances only
 * as far as HM_REVIEW and humans set the terminal states.
 *
 * OFFERED (added 2026-10-02, Nachi-approved) is the one exception to "machine
 * advances only as far as HM_REVIEW": CandidateOfferEventService auto-sets it
 * when an OFFER_MADE event is recorded. It's a pure workflow label with no
 * evidentiary/billing weight - the actual billing trigger is the append-only
 * offer-event row, not this status.
 *
 * The mockup's *treatment* is what is being taken -- a horizontal numbered
 * track rather than a vertical list -- not its labels.
 */
const PIPELINE: ReadonlyArray<{ status: Status; label: string }> = [
  { status: Status.NEW, label: 'New' },
  { status: Status.REVIEWED, label: 'Reviewed' },
  { status: Status.INVITATION_SENT, label: 'Invited' },
  { status: Status.RIO_SCREENING, label: 'Rio screening' },
  { status: Status.ALT_INTERVIEW, label: 'Alt interview' },
  { status: Status.HM_REVIEW, label: 'HM review' },
  { status: Status.OFFERED, label: 'Offered' },
];

/**
 * Terminal states sit outside the track rather than as a seventh stage.
 *
 * They are not a further step along it: a candidate can be rejected from any
 * stage, and drawing "Rejected" as the end of a progress bar implies everyone
 * walks to it. Shown as an outcome chip beneath instead, in the colour the
 * state actually means -- which is the palette the design system does not
 * define and this page carried as a bare literal until now.
 */
const TERMINAL: Partial<
  Record<Status, { label: string; fg: string; bg: string }>
> = {
  [Status.HIRED]: {
    label: 'Hired',
    fg: statusPalette.positive,
    bg: statusPalette.positiveBg,
  },
  [Status.REJECTED]: {
    label: 'Rejected',
    fg: statusPalette.negative,
    bg: statusPalette.negativeBg,
  },
  [Status.NOT_INTERESTED]: {
    label: 'Not interested',
    fg: statusPalette.caution,
    bg: statusPalette.cautionBg,
  },
};

export interface PipelineTrackProps {
  status?: string;
}

export const PipelineTrack = memo(({ status }: PipelineTrackProps) => {
  const terminal = status ? TERMINAL[status as Status] : undefined;

  /**
   * Index of the stage the candidate is on. -1 when the status is terminal or
   * unrecognised, which leaves every marker in its "not reached" state rather
   * than guessing a position — an unknown status should not light up a stage.
   */
  const currentIndex = useMemo(
    () => PIPELINE.findIndex((s) => s.status === status),
    [status],
  );

  return (
    <div>
      <div className={styles.track} role="list" aria-label="Candidate pipeline">
        {PIPELINE.map((stage, i) => {
          const done = currentIndex > i;
          const current = currentIndex === i;
          return (
            <div
              key={stage.status}
              role="listitem"
              aria-current={current ? 'step' : undefined}
              className={`${styles.stage} ${done ? styles.stageDone : ''}`}
            >
              <span
                className={`${styles.marker} ${done ? styles.markerDone : ''} ${
                  current ? styles.markerCurrent : ''
                }`}
                aria-hidden="true"
              >
                {done ? '✓' : i + 1}
              </span>
              <span
                className={`${styles.label} ${current ? styles.labelCurrent : ''}`}
              >
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>

      {terminal ? (
        <span
          className={styles.terminal}
          style={{ color: terminal.fg, background: terminal.bg }}
        >
          {terminal.label}
        </span>
      ) : null}
    </div>
  );
});

PipelineTrack.displayName = 'PipelineTrack';

export default PipelineTrack;
